import { useState } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useSelector, useDispatch } from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import {
  AppBar,
  Box,
  Button,
  Grid,
  IconButton,
  Toolbar,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UnlockEstimateComponent from 'components/UnlockEstimateComponent';
import LeadFormComponent from 'components/LeadFormComponent';
import FullScreenDialog from 'components/FullScreenDialog';
import PriceComponent from 'components/PriceComponent';
import SummaryContentComponent from 'components/SummaryComponent/SummaryContentComponent';

import { getSummary } from 'utils/summaryUtils';
import { getSelectedCourses } from 'utils/courseUtils';
import {
  getSelectedAccommodationGroups,
  unsetAccommodationGroups,
} from 'utils/accommodationUtils';
import {
  getLocationInsurances,
  getSelectedInsurances,
} from 'utils/insuranceUtils';
import { getSelectedPackage } from 'utils/packageUtils';
import { getSelectedAdditionalServices } from 'utils/additionalServicesUtils';
import { shouldUnlockPrice } from 'utils/lead';

import { OpenCloseDialogLead } from 'store/actions/schoolDetailActions';

const useStyles = makeStyles(() => ({
  appBarBottom: {
    bottom: 0,
    top: 'auto',
  },

  blur: {
    filter: 'blur(5px)',
  },
}));

function SummaryComponent({
  createOrder,
  dateParams,
  isSalesExecutive,
  isFetching,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useUser();
  const courseWeekQuantity = dateParams.courseWeeks;

  const { lead } = useSelector((store) => store.studentReducer);
  const {
    accommodationGroups,
    additionalServices,
    courses,
    currency,
    location,
    needAccommodation,
    openDialogLead,
  } = useSelector((store) => store.schoolDetailReducer);
  const [closeWidgetSalesExecutive, setCloseWidgetSalesExecutive] =
    useState(false);
  const { detail } = useSelector((store) => store.packageReducer);
  const selectedPackage = getSelectedPackage(detail);
  const [openDialog, setOpenDialog] = useState(false);
  const [specialistDialog, setSpecialistDialog] = useState(false);

  const estimateTitle = specialistDialog
    ? 'Falar com um especialista'
    : 'Desbloquear oferta especial';

  const selectedCourses = getSelectedCourses(courses);

  if (!needAccommodation) unsetAccommodationGroups(accommodationGroups);

  const selectedAccommodationGroups =
    getSelectedAccommodationGroups(accommodationGroups);
  const selectedAdditionalServices = getSelectedAdditionalServices(
    additionalServices,
    courseWeekQuantity,
    accommodationGroups.some((ag) => ag.selected)
  );
  const selectedInsurances = getSelectedInsurances(
    getLocationInsurances(location)
  );
  const summaryCurrency = currency.id > 0 ? currency : detail.currency;
  const selectedItems = {
    accommodationGroups: selectedAccommodationGroups,
    additionalServices: selectedAdditionalServices,
    courseWeeks: courseWeekQuantity,
    courses: selectedCourses,
    currency: summaryCurrency,
    insurances: selectedInsurances,
    packages: selectedPackage,
  };

  const summary = getSummary(selectedItems);

  return (
    <>
      <Box display={{ xs: 'none', md: 'block' }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SummaryContentComponent
              createOrder={createOrder}
              dateParams={dateParams}
              isSalesExecutive={isSalesExecutive}
              isFetching={isFetching}
              summary={summary}
            />
          </Grid>

          {shouldUnlockPrice(lead, user) &&
            !isSalesExecutive &&
            !lead?.hasMeetingScheduled && (
              <Grid item xs={12}>
                <Box textAlign="center">
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      OpenCloseDialogLead(dispatch, true);
                      setSpecialistDialog(true);
                      setCloseWidgetSalesExecutive(true);
                    }}
                    fullWidth
                    className={classes.talkToASpecialist}
                  >
                    Quero falar com um Especialista
                  </Button>
                </Box>
              </Grid>
            )}
        </Grid>
      </Box>

      <Box display={{ xs: 'block', md: 'none' }}>
        <AppBar
          position="fixed"
          color="secondary"
          className={classes.appBarBottom}
        >
          <Toolbar>
            {!shouldUnlockPrice(lead, user) ? (
              <Grid container alignItems="center" justifyContent="center">
                <Grid item xs={12}>
                  <Typography textAlign="center" mb={2} mt={3} fontWeight={600}>
                    Desbloqueie as ofertas especiais e<br /> faça seu orçamento
                    online
                  </Typography>
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{ marginBottom: '24px', textAlign: 'center' }}
                >
                  <UnlockEstimateComponent />
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item xs={1}>
                  <Box>
                    <IconButton
                      edge="start"
                      size="small"
                      onClick={() => {
                        setOpenDialog(true);
                      }}
                    >
                      <Box color="primary.contrastText">
                        <ExpandLessIcon />
                      </Box>
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    my={2}
                    onClick={() => {
                      setOpenDialog(true);
                    }}
                  >
                    <Typography variant="subtitle2">Valor Total:</Typography>
                    <Typography variant="h4">
                      <Box fontWeight="bold">
                        {summary.totalDiscountExchanged ? (
                          <PriceComponent
                            value={summary.totalDiscountExchanged}
                          />
                        ) : (
                          <>-</>
                        )}
                      </Box>
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={5}>
                  <Button
                    color="primary"
                    disabled={isFetching}
                    fullWidth
                    id="book-summary-detail"
                    onClick={createOrder}
                    variant="contained"
                  >
                    Reservar
                  </Button>
                </Grid>
              </Grid>
            )}

            <FullScreenDialog
              open={openDialog}
              setOpen={(open) => setOpenDialog(open)}
            >
              <SummaryContentComponent
                createOrder={createOrder}
                dateParams={dateParams}
                isSalesExecutive={isSalesExecutive}
                isFetching={isFetching}
                open={openDialog}
                setOpen={(open) => setOpenDialog(open)}
                summary={summary}
              />
            </FullScreenDialog>
          </Toolbar>
        </AppBar>
      </Box>

      <FullScreenDialog
        maxWidth="sm"
        open={openDialogLead}
        setOpen={(open) => OpenCloseDialogLead(dispatch, open)}
        title={estimateTitle}
        disableOnClose={!closeWidgetSalesExecutive}
        showCloseButton={closeWidgetSalesExecutive}
        disableEscapeKeyDown={closeWidgetSalesExecutive}
      >
        <LeadFormComponent
          cityId={location.city.id}
          countryId={location.city.country.id}
          course={selectedCourses}
          courseId={selectedCourses.id}
          id="summary-package-widget"
          lead={lead}
          onSubmitSecondStep={() => {
            setCloseWidgetSalesExecutive(true);
          }}
          onMessageSuccess={() => {
            setCloseWidgetSalesExecutive(true);
          }}
          shouldScheduleMeeting={lead?.step === 3 && !lead?.hasMeetingScheduled}
        />
      </FullScreenDialog>
    </>
  );
}

export default SummaryComponent;

SummaryComponent.propTypes = {
  createOrder: PropTypes.instanceOf(Object).isRequired,
  dateParams: PropTypes.instanceOf(Object).isRequired,
  isSalesExecutive: PropTypes.bool,
  isFetching: PropTypes.bool,
};

SummaryComponent.defaultProps = {
  isSalesExecutive: false,
  isFetching: false,
};
